import cx from 'classnames';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AxisDomain } from 'recharts/types/util/types';

import { useWindowResized } from '../../../../../features/app';
import { useChartMeasurements, useXAxisTicks, useYAxisTicks } from '../../../../../features/chart';
import { usePhysiologicalData, useResetToDefaultViewAndRange } from '../../../../../features/physiological-data';
import { ChartTooltip, YAxisTick } from './components';
import { ChartDot } from './components/chart-dot';
import { XAxisTick } from './components/x-axis-tick';
import { IPressureChartProps } from './types';

export const PressureChart = ({ wrapperRef }: IPressureChartProps) => {
  const { t } = useTranslation();
  const resetToDefaultViewAndRange = useResetToDefaultViewAndRange();
  const windowResized = useWindowResized();

  const containerWidth = useMemo(() => {
    if (!wrapperRef.current) return 0;
    const { width } = wrapperRef.current.getBoundingClientRect();
    const padding = 48;
    return width - padding;
  }, [wrapperRef.current, windowResized]);

  const { physiologicalData, rangeType } = usePhysiologicalData();
  const measurements = useChartMeasurements();

  const yAxisTicks = useYAxisTicks();
  const xAxisTicks = useXAxisTicks();

  useEffect(() => {
    resetToDefaultViewAndRange();
  }, []);

  return (
    <div className={cx('flex items-center justify-center', rangeType)}>
      <ResponsiveContainer>
        <div className="bg-background-base-primary h-full">
          <LineChart width={containerWidth} height={450} margin={{ top: 16, bottom: 16 }} data={measurements}>
            <CartesianGrid stroke="#00000030" strokeDasharray="2" vertical={false} />
            <Line
              type="linear"
              dataKey="sys"
              strokeWidth={3}
              stroke="#017c7e"
              dot={(props) => <ChartDot {...props} type="sys" />}
              activeDot={false}
              isAnimationActive={false}
            />
            <Line
              type="linear"
              dataKey="dia"
              strokeWidth={3}
              stroke="#017c7e"
              dot={(props) => <ChartDot {...props} type="dia" />}
              activeDot={false}
              isAnimationActive={false}
            />
            <XAxis
              ticks={xAxisTicks}
              tick={<XAxisTick />}
              tickLine={false}
              axisLine={false}
              dataKey="date"
              interval={0}
              padding={{ left: 20, right: 30 }}
            />
            <YAxis
              domain={[yAxisTicks[0], yAxisTicks[yAxisTicks.length - 1]] as AxisDomain}
              ticks={yAxisTicks}
              tick={<YAxisTick />}
              tickLine={false}
              axisLine={false}
              orientation="right"
            />
            {measurements.length !== 0 && (
              <Tooltip
                content={<ChartTooltip wrapperRef={wrapperRef} />}
                wrapperStyle={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  left: '-11px',
                  width: '2px',
                  height: 'calc(100% - 62px)',
                  backgroundColor: '#011E1F',
                  outline: 'none',
                }}
                cursor={false}
                allowEscapeViewBox={{ x: true }}
              />
            )}
          </LineChart>
        </div>
      </ResponsiveContainer>

      {Object.keys(physiologicalData?.measurements ?? {}).length === 0 && (
        <div className="absolute p-2 bg-background-surface-alt rounded-lg typography-title-3 text-text-primary">
          {t('patient.chart.empty')}
        </div>
      )}
    </div>
  );
};
